import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useControllableValue, useMemoizedFn, useSize } from 'ahooks';
import { isEqual } from 'lodash';
import { Message } from '@bedrock/components';
import { handleQuoteColumns, QuoteTableType } from './handleQuoteColumns';
import { ProTablePlusForm } from '#components';
import { apiService, defs } from '#services';
import classNames from 'classnames/bind';
import styles from './index.less';
import RowCheckbox from './RowCheckbox';

const cx = classNames.bind(styles);

type Props = Omit<
  React.ComponentProps<typeof ProTablePlusForm>,
  'columns' | 'onBatchEditChange'
> & {
  quoteTableType: QuoteTableType;
  quoteTableColumns: defs.DemandQuoteTableColumnDto[];
  cooperateAccountList: defs.DemandCooperateAccountDto[];
  onBatchEditChange?: (data: any[]) => void;
  readOnly?: boolean;
  templateType?: number;
  addTemplateList?: any[]; // 添加模板列表,通过cooperateAccounId匹配
  mergeColumns?: (columns: any[]) => any[];
  batchDeleteMode?: boolean;
  skipNeedAiValidation?: boolean; // 拆单场景下不校验 need_ai 行
};

type Ref = React.ComponentRef<typeof ProTablePlusForm> & {
  initData: (data: any[]) => void;
  unEditableColumns: any[];
  setSelectedRows: (arr: any[]) => void;
  handleBatchDeleteRows: () => void;
};

// 根据需求单报价模板生成报价表格
const DemandConfigQuoteTable = forwardRef<Ref, Props>((props, ref) => {
  // const [value, onChange] = useControllableValue(props);
  const {
    value,
    onChange,
    templateType,
    quoteTableColumns,
    cooperateAccountList,
    quoteTableType = 'supplierQuote',
    readOnly,
    addTemplateList = [],
    mergeColumns,
    batchDeleteMode,
    skipNeedAiValidation,
    ...restProps
  } = props;
  const rowKey = templateType === 1 ? 'l2$subDataId' : 'quoteKey';
  const tableRef = useRef<React.ComponentRef<typeof ProTablePlusForm>>(null);
  const [unEditableColumns, setUnEditableColumns] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState([]);

  const renderCheckboxCol = useMemoizedFn(({ nodeLevel, currentLevel, mergeRowSpanKey, nodeKey }) => {
    if (currentLevel) return null;
    return (batchDeleteMode && currentLevel === 0) ? {
      title: <RowCheckbox.Head onSelectChange={setSelectedRows} data={value} selectedRows={selectedRows} rowKey={nodeKey} />,
      width: 50,
      dataIndex: `$l${nodeLevel}_checkbox`,
      fixed: currentLevel === 0 ? 'right' : '',
      mergeRowSpanKey: mergeRowSpanKey,
      render: (v: any, row: any, index: number) => {
        return <RowCheckbox.Row onSelectChange={setSelectedRows} record={row} selectedRows={selectedRows} rowKey={nodeKey}  />
      }
    } : null;
  });

  const quoteColumns = useMemo(() => {
    if (!quoteTableColumns?.length) return [];
    const {
      columns,
      unEditableColumns,
    } = handleQuoteColumns({
      readOnly,
      quoteTableType,
      tableRef,
      cooperateAccountList,
      headerColumns: quoteTableColumns,
      addTemplateList: addTemplateList,
      renderCheckboxCol: renderCheckboxCol,
    });
    setUnEditableColumns(unEditableColumns);
    return mergeColumns ? mergeColumns(columns) : columns;
  }, [quoteTableColumns, cooperateAccountList, mergeColumns, batchDeleteMode, selectedRows]);
  const localData = useRef(value);

  const handleBatchDeleteRows = useMemoizedFn(() => {
    tableRef.current?.treeDeleteRowByIds?.(selectedRows.map(item => item.quoteKey))
  });

  const onLocalChange = useMemoizedFn((newData) => {
    if (isEqual(localData.current, newData)) return;
    localData.current = newData;
    onChange?.(newData);
  });

  // 虚拟滚动场景下需要根据
  const allValidate = useMemoizedFn(() => {
    // 判断是否触发虚拟滚动
    if (!tableRef.current.getIsVirtualScroll()) {
      return tableRef.current.validate();
    }
    const vMap = tableRef.current.getValidateRulesMap('all') as Map<string, any[]>;
    // 打印长度
    console.log('报价表格 校验规则长度', vMap.size);
    return Promise.all(Array.from(vMap.values()).map((v: any) =>{
      const row = value[v.rowIndex]
      if(!row) return false;
      if(row?.$addRow?.length === 1) {
        return false;
      }
      if(row?.$addRow?.length === 2 && v?.columnId?.includes('l2$')) {
        return false;
      }
      return new Promise((resolve, reject) => {
        v?.doValidate?.().then(n => {
          resolve(n);
        })
      })
    })).then(n => n.filter((v: any) => {
      if(!v) return false
      // 判断一级添加行
      if(v?.row?.$addRow?.length === 1) {
        return false;
      }
      if(v?.row?.$addRow?.length === 2 && v?.dataIndex?.includes('l2$')) {
        return false;
      }
      return true;
    }));
  })

  // 校验 need_ai 合作内容中是否至少有一行包含 AI
  const validateNeedAiRows = useMemoizedFn(() => {
    const needAiAccounts = cooperateAccountList?.filter((account) => account.need_ai);
    if (!needAiAccounts?.length) return null;

    const tableData = localData.current || [];
    for (const account of needAiAccounts) {
      const rows = tableData.filter((row) => row.cooperateAccountId === account.id && !row.$addRow);
      const hasAiRow = rows.some(
        (row) => row.isUseAi === '是' || row.isUseAi === true,
      );
      if (rows?.length > 0 && !hasAiRow) {
        return `合作内容${account.index_id ?? ''}（需包含AI技术）至少需要有一行「是否使用AI」选择「是」`;
      }
    }
    return null;
  });

  const validateFieldsAndScroll = useMemoizedFn(() => {
    return new Promise((resolve, reject) => {
      Message.loading({
        content: '正在校验数据, 请稍后...',
        duration: 0,
      }).then((hide) => {
        // 定时手动关闭
        setTimeout(() => {
          console.time('报价表格 校验');
          allValidate().then((err) => {
            if (err?.length) {
              reject(err);
              console.log('err', err);
              tableRef.current?.scrollTo?.({
                key: err[0].rowKey,
                dataIndex: err[0].dataIndex,
              })
              setTimeout(() => {
                tableRef.current.validateFields()
              }, 100);
            } else {
              const needAiError = skipNeedAiValidation ? null : validateNeedAiRows();
              if (needAiError) {
                Message.error(needAiError);
                reject(needAiError);
              } else {
                console.log('校验成功');
                resolve('');
              }
            }
          }).finally(() => {
            hide();
            console.timeEnd('报价表格 校验');
          });
        }, 400);
      })
    });
  });

  // 根据服务端返回的错误信息，滚动到错误行，并校验（待完成）
  const scrollToErrorLineAndValidateFields = useMemoizedFn((err: any[]) => {
    tableRef.current?.scrollTo?.({
      key: err[0].rowKey,
      dataIndex: err[0].dataIndex,
    })
    setTimeout(() => {
      tableRef.current.validateFields()
    }, 100);
  });

  useImperativeHandle(ref, () => {
    if (!tableRef.current) return {} as Ref;
    return {
      ...tableRef.current,
      initData: (data) => {
        tableRef.current.treeInitData(data, quoteColumns);
      },
      unEditableColumns,
      validateFields: validateFieldsAndScroll,
      handleBatchDeleteRows,
      setSelectedRows,
    };
  });
  const onBatchEditChange = useMemoizedFn((newData) => {
    Message.info('批量编辑成功');
  });

  /** 文件上传中时不允许滚动 */
  const disabledScrolled = value?.some?.(item => {
    return Object.keys(item)?.some?.(key => {
      const val = item[key];
      return val?.files?.some?.(file => {
        return file?.status === 'uploading';
      })
    })
  })

  return (
    <div>
      {!!quoteColumns?.length && (
        <ProTablePlusForm
          className={cx('quote-table')}
          readOnly={readOnly}
          rowKey={(row) => {
            return  `${row.cooperateAccountId}-${row.quoteKey}-${row.l2$subDataId}`;
          }}
          style={{ width: '100%' }}
          scroll={{ x: 'max-content', y: 600 }}
          virtual={{ maxRows: 30, useAntd: true, itemHeight: 70, disabledScrolled }}
          customScrollbarSize={6}
          verticalBorder
          {...restProps}
          onChange={onLocalChange}
          ref={tableRef}
          value={value}
          columns={quoteColumns}
          onBatchEditChange={onBatchEditChange}
        />
      )}
    </div>
  );
});

export default DemandConfigQuoteTable;
